import React from 'react';
import { Box, Button } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { isPdf } from '../../services/Functions';
import { documentExtensions } from '../../utils/PortalName';

export const isPdfDocument = (src) => {
    if (!src || typeof src !== 'string') return false;
    if (isPdf(src)) return true;
    if (src.startsWith('data:application/pdf')) return true;
    if (src.toLowerCase().includes('application/pdf')) return true;
    if (src.toLowerCase().endsWith('.pdf')) return true;
    return false;
};

export const ReuploadDocumentThumbnail = ({
    src,
    alt = 'Document',
    className = 'doc-reupload-slot__thumb',
    onClick,
}) => {
    if (!src) return null;

    if (isPdfDocument(src)) {
        const openPdf = () => {
            if (onClick) {
                onClick();
                return;
            }
            window.open(src, '_blank', 'noopener,noreferrer');
        };

        return (
            <button
                type="button"
                className="doc-reupload-slot__pdf-thumb"
                onClick={openPdf}
                aria-label={alt}
            >
                <PictureAsPdfIcon className="doc-reupload-slot__pdf-icon" />
                <span className="doc-reupload-slot__pdf-label">PDF</span>
            </button>
        );
    }

    return (
        <img
            src={src}
            alt={alt}
            className={className}
            onClick={onClick}
        />
    );
};

export const REUPLOAD_STEP_PAGE_NAME = 'Re-Upload';
export const PP_REUPLOAD_STEP_PAGE_NAME = 'PP Re-Upload';

export const ReuploadDocSectionLabel = ({ children }) => (
    <div className="doc-grid-section-label fieldLabel doc-grid-label">{children}</div>
);

export const ReuploadDocumentsGrid = ({ children }) => (
    <div className="doc-grid-uploads">{children}</div>
);

export const isDocFlagRejected = (flag) => Number(flag) === -1;
export const isDocFlagApproved = (flag) => Number(flag) === 1;
export const isDocFlagPending = (flag) => !isDocFlagApproved(flag) && !isDocFlagRejected(flag);

/** Keep server document when user did not replace it on re-upload */
export const preserveReuploadDocumentValue = (currentValue, originalValue) => {
    const current = currentValue != null ? String(currentValue).trim() : '';
    if (current) return current;
    const original = originalValue != null ? String(originalValue).trim() : '';
    return original || '';
};

/** Show slot when document exists, is under review, or is required for this application */
export const shouldShowReuploadDocumentSlot = ({
    documentValue,
    flag,
    required = false,
}) => {
    if (required) return true;
    if (documentValue) return true;
    if (isDocFlagRejected(flag) || isDocFlagApproved(flag)) return true;
    return false;
};

/** Only rejected documents can be replaced on re-upload pages */
export const isReuploadDocumentEditable = (flag) => isDocFlagRejected(flag);

/** Legacy re-upload forms: block approved (1), allow null/0 and rejected (-1) */
export const isLegacyReuploadDocumentEditable = (flag) => {
    if (isDocFlagApproved(flag)) return false;
    return !flag || isDocFlagRejected(flag);
};

export const ReuploadApprovedReadonlyNote = ({ flag }) => {
    if (!isDocFlagApproved(flag)) return null;
    return (
        <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
            {renderReuploadStatus(true, false, false)}
            <span className="doc-reupload-slot__readonly-hint">Cannot be changed</span>
        </Box>
    );
};

export const LegacyServerDocumentPreview = ({ thumbnail, flag, alt = 'Document' }) => (
    <>
        <img src={thumbnail} style={{ width: '50%' }} alt={alt} />
        <ReuploadApprovedReadonlyNote flag={flag} />
    </>
);

export const buildReuploadStatusBar = (isComplete) => ([{
    title1: 'RE-UPLOAD',
    title2: 'DOCUMENTS',
    status: isComplete ? 1 : 0,
}]);

/** Stepper turns blue only when every rejected document has been re-uploaded */
export const buildReuploadStatusBarFromSlots = (slots = []) => {
    const rejected = slots.filter((slot) => slot.isRejected);
    const complete = rejected.length === 0 || rejected.every((slot) => slot.isReUploaded);
    return buildReuploadStatusBar(complete);
};

export const isReuploadSubmitDisabled = (slots = [], submitCompleted = false) => {
    if (submitCompleted) return true;
    return slots.some((slot) => slot.isRejected && !slot.isReUploaded);
};

const pickReuploadField = (sources, keys = []) => {
    for (const source of sources) {
        if (!source) continue;
        for (const key of keys) {
            const value = source[key];
            if (value !== undefined && value !== null && String(value).trim() !== '') {
                return value;
            }
        }
    }
    return null;
};

export const REUPLOAD_DOCUMENT_URL_KEYS = {
    cnicFront: ['applicantCNICFront', 'copyOfApplicantCNICFront'],
    cnicBack: ['applicantCNICBack', 'copyOfApplicantCNICBack'],
    applicantCnic: ['copyOfApplicantCNIC', 'applicantCNIC'],
    ownership: ['owenershipDocument', 'ownershipImage', 'owenershipImage', 'ownershipDocument'],
    electricityBill: [
        'electricityBillDocument',
        'electricityBill',
        'electricityBillImage',
        'scannedCopyOfNearByElectricityBill',
    ],
    property: ['premisesDocument', 'copyOfPropertyDocument', 'propertyDocument'],
    paidBill: ['paidBillImage', 'applicantLastPaidBill'],
    fbr: ['fbrDocument'],
    anyOther: ['anyOtherDocument', 'addDocAnyOther'],
    ownerCnicFront: ['copyOfOwnerCNICFront', 'preOwnerCNICFront'],
    ownerCnicBack: ['copyOfOwnerCNICBack', 'preOwnerCNICBack'],
    ownerNoc: ['ownerNOC', 'perOwenerNOC'],
    resolution: ['resolutionFormDesc'],
    memorandum: ['certMemoDesc'],
    form29: ['certFormDesc'],
};

export const pickReuploadDocumentUrl = (sources, keys = []) => {
    const sourceList = Array.isArray(sources) ? sources : [sources];
    return pickReuploadField(sourceList, keys) || '';
};

const pickReuploadFlag = (sources, keys = []) => {
    for (const source of sources) {
        if (!source) continue;
        for (const key of keys) {
            if (!Object.prototype.hasOwnProperty.call(source, key)) continue;
            const value = source[key];
            if (value !== undefined && value !== null && value !== '') {
                return value;
            }
        }
    }
    return null;
};

/** Rejected-document remark fields used across re-upload services */
const REUPLOAD_REJECTED_REMARK_DEFINITIONS = [
    {
        label: 'CNIC Front',
        flagKeys: ['applicantCNICFrontFlag', 'copyOfApplicantCNICFrontFlag'],
        remarkKeys: [
            'applicantCNICFrontRemarks',
            'applicantCNICFrontObjectionRemarks',
            'copyOfApplicantCNICFrontRemarks',
            'copyOfApplicantCNICFrontObjectionRemarks',
        ],
    },
    {
        label: 'CNIC Back',
        flagKeys: ['applicantCNICBackFlag', 'copyOfApplicantCNICBackFlag'],
        remarkKeys: [
            'applicantCNICBackRemarks',
            'applicantCNICBackObjectionRemarks',
            'copyOfApplicantCNICBackRemarks',
            'copyOfApplicantCNICBackObjectionRemarks',
        ],
    },
    {
        label: "Applicant's CNIC",
        flagKeys: ['copyOfApplicantCNICFlag', 'applicantCNICFlag'],
        remarkKeys: ['applicantCNICRemarks', 'copyOfApplicantCNICRemarks', 'copyOfApplicantCNICObjectionRemarks'],
    },
    {
        label: 'Ownership Document',
        flagKeys: ['ownershipImageFlag', 'owenershipDocumentFlag'],
        remarkKeys: [
            'ownershipImageRemarks',
            'owenershipDocumentRemarks',
            'owenershipObjectionRemarks',
            'ownershipObjectionRemarks',
        ],
    },
    {
        label: 'Electricity Bill',
        flagKeys: ['electricityBillFlag', 'electricityBillImageFlag'],
        remarkKeys: [
            'electricityBillRemarks',
            'electricityBillImageRemarks',
            'electricityBillObjectionRemarks',
        ],
    },
    {
        label: 'Near-by Electricity Bill',
        flagKeys: ['scannedCopyOfNearByElectricityBillFlag'],
        remarkKeys: [
            'scannedCopyOfNearByElectricityBillRemarks',
            'scannedCopyOfNearByElectricityBillObjectionRemarks',
        ],
    },
    {
        label: 'Property Documents',
        flagKeys: ['premisesDocumentFlag'],
        remarkKeys: ['premisesDocumentRemarks', 'premisesDocumentObjectionRemarks'],
    },
    {
        label: 'Paid Bill Image',
        flagKeys: ['paidBillImageFlag', 'applicantLastPaidBillFlag'],
        remarkKeys: [
            'paidBillImageRemarks',
            'paidBillImageObjectionRemarks',
            'applicantLastPaidBillRemarks',
        ],
    },
    {
        label: 'FBR Document',
        flagKeys: ['fbrDocumentFlag'],
        remarkKeys: ['fbrDocumentRemarks', 'fbrDocumentObjectionRemarks'],
    },
    {
        label: 'Any Other Document',
        flagKeys: ['anyOtherDocumentFlag'],
        remarkKeys: ['anyOtherDocumentRemarks', 'anyOtherDocumentObjectionRemarks'],
    },
    {
        label: 'Owner CNIC Front',
        flagKeys: ['copyOfOwnerCNICFrontFlag', 'preOwnerCNICFrontFlag'],
        remarkKeys: [
            'copyOfOwnerCNICFrontRemarks',
            'copyOfOwnerCNICFrontObjectionRemarks',
            'preOwnerCNICFrontRemarks',
            'preOwnerCNICFrontObjectionRemarks',
        ],
    },
    {
        label: 'Owner CNIC Back',
        flagKeys: ['copyOfOwnerCNICBackFlag', 'preOwnerCNICBackFlag'],
        remarkKeys: [
            'copyOfOwnerCNICBackRemarks',
            'copyOfOwnerCNICBackObjectionRemarks',
            'preOwnerCNICBackRemarks',
            'preOwnerCNICBackObjectionRemarks',
        ],
    },
    {
        label: 'NOC from Owner',
        flagKeys: ['ownerNOCFlag', 'perOwenerNOCFlag'],
        remarkKeys: ['ownerNOCRemarks', 'ownerNOCObjectionRemarks', 'perOwenerNOCObjectionRemarks'],
    },
    {
        label: "BoD's Resolution Form",
        flagKeys: ['resolutionFormDescFlag'],
        remarkKeys: ['resolutionFormDescRemarks', 'resolutionFormDescObjectionRemarks'],
    },
    {
        label: 'Memorandum',
        flagKeys: ['certMemoDescFlag'],
        remarkKeys: ['certMemoDescRemarks', 'certMemoDescObjectionRemarks'],
    },
    {
        label: 'Form-29',
        flagKeys: ['certFormDescFlag'],
        remarkKeys: ['certFormDescRemarks', 'certFormDescObjectionRemarks'],
    },
    {
        label: 'Test Report',
        flagKeys: ['testReportFlag'],
        remarkKeys: ['testReportRemarks', 'testReportObjectionRemarks'],
    },
    {
        label: 'Authority Letter',
        flagKeys: ['authorityLetterFlag'],
        remarkKeys: ['authorityLetterRemarks', 'authorityLetterObjectionRemarks'],
    },
    {
        label: 'Other Documents',
        flagKeys: ['otherDocumentsFlag'],
        remarkKeys: ['otherDocumentsRemarks', 'otherDocumentsObjectionRemarks'],
    },
    {
        label: 'Wiring Test Report',
        flagKeys: ['wiringTestReportFlag', 'wiringTestReportDocumentFlag'],
        remarkKeys: [
            'wiringTestReportRemarks',
            'wiringTestReportObjectionRemarks',
            'wiringTestReportDocumentRemarks',
            'wiringTestReportDocumentObjectionRemarks',
        ],
    },
];

/** Success popup: list every rejected document and its objection remarks */
export const buildReuploadDocumentRemarks = (documents = {}, dialogRowData = {}) => {
    const sources = [documents, dialogRowData];
    const seenLabels = new Set();
    const results = [];

    REUPLOAD_REJECTED_REMARK_DEFINITIONS.forEach((definition) => {
        const flag = pickReuploadFlag(sources, definition.flagKeys);
        if (!isDocFlagRejected(flag)) return;
        if (seenLabels.has(definition.label)) return;

        const remarks = pickReuploadField(sources, definition.remarkKeys);
        const text = remarks != null ? String(remarks).trim() : '';

        seenLabels.add(definition.label);
        results.push({
            label: definition.label,
            remarks: text || '—',
        });
    });

    return results;
};

export const getReuploadSlotClass = (isApproved, isRejected, isReUploaded, isPending) => {
    if (isReUploaded) return 'doc-reupload-slot doc-reupload-slot--changed';
    if (isApproved) return 'doc-reupload-slot doc-reupload-slot--approved';
    if (isRejected) return 'doc-reupload-slot doc-reupload-slot--rejected';
    if (isPending) return 'doc-reupload-slot doc-reupload-slot--pending';
    return 'doc-reupload-slot';
};

export const renderReuploadStatus = (isApproved, isRejected, isReUploaded, isPending) => {
    if (isReUploaded) {
        return (
            <span className="doc-reupload-slot__status doc-reupload-slot__status--changed">
                <CheckCircleIcon sx={{ fontSize: 14 }} />
                Changed
            </span>
        );
    }
    if (isApproved) {
        return (
            <span className="doc-reupload-slot__status doc-reupload-slot__status--approved">
                <CheckCircleIcon sx={{ fontSize: 14 }} />
                Approved
            </span>
        );
    }
    if (isRejected) {
        return (
            <span className="doc-reupload-slot__status doc-reupload-slot__status--rejected">
                <CancelIcon sx={{ fontSize: 14 }} />
                Rejected
            </span>
        );
    }
    if (isPending) {
        return (
            <span className="doc-reupload-slot__status doc-reupload-slot__status--pending">
                Pending Review
            </span>
        );
    }
    return null;
};

export const ReuploadDocumentSlot = ({
    label,
    thumbnail,
    documentSources = [],
    documentUrlKeys = [],
    isApproved,
    isRejected,
    isReUploaded,
    inputId,
    inputName,
    onUpload,
    uploadError,
    thumbAlt,
}) => {
    const isPending = !isApproved && !isRejected;
    const isReadOnly = isApproved || isPending;
    const canChange = isRejected && !isReUploaded;
    const resolvedThumbnail = thumbnail
        || (documentUrlKeys.length ? pickReuploadDocumentUrl(documentSources, documentUrlKeys) : '');

    return (
        <div
            className={getReuploadSlotClass(isApproved, isRejected, isReUploaded, isPending)}
            aria-readonly={isReadOnly || undefined}
        >
            <div className="field-wrap">
                <label className="fieldLabel">
                    {label}
                    {renderReuploadStatus(isApproved, isRejected, isReUploaded, isPending)}
                </label>
            </div>
            <div className={`doc-reupload-slot__preview${isReadOnly ? ' doc-reupload-slot__preview--readonly' : ''}`}>
                {resolvedThumbnail ? (
                    <div className={`doc-reupload-slot__thumb-wrap${isReadOnly ? ' doc-reupload-slot__thumb-wrap--readonly' : ''}`}>
                        <ReuploadDocumentThumbnail
                            src={resolvedThumbnail}
                            alt={thumbAlt || label}
                        />
                    </div>
                ) : isReadOnly ? (
                    <div className="doc-reupload-slot__readonly-placeholder">
                        {isApproved ? 'Approved document on file' : 'Document on file'}
                    </div>
                ) : null}
                {canChange ? (
                    <Button
                        variant="outlined"
                        component="label"
                        size="small"
                        color={isReUploaded ? 'success' : 'error'}
                        className="change-btn"
                        startIcon={isReUploaded ? <CheckCircleIcon /> : <FileUploadOutlinedIcon />}
                    >
                        {isReUploaded ? 'Changed' : 'Change'}
                        <input
                            hidden
                            accept={documentExtensions}
                            id={inputId}
                            name={inputName}
                            type="file"
                            onChange={onUpload}
                        />
                    </Button>
                ) : null}
            </div>
            {isApproved ? (
                <p className="doc-reupload-slot__readonly-hint">This document is approved and cannot be changed.</p>
            ) : null}
            {isPending && !isApproved ? (
                <p className="doc-reupload-slot__readonly-hint">Pending review — this document will be sent as-is.</p>
            ) : null}
            {uploadError ? <Box className="documentError">{uploadError}</Box> : null}
        </div>
    );
};
